import { motion } from "framer-motion";
import {
  Buildings,
  AirplaneTakeoff,
  MapTrifold,
  UsersThree,
  GraduationCap,
  Tooth,
  Briefcase,
  HouseLine,
} from "@phosphor-icons/react";
import { Reveal, SectionLabel } from "@/components/Reveal";

const IMG = {
  tours: "https://images.pexels.com/photos/9223235/pexels-photo-9223235.jpeg",
  hotel: "https://images.pexels.com/photos/17426635/pexels-photo-17426635.jpeg",
  transport: "https://images.unsplash.com/photo-1616455436127-553454f32623",
  corporate: "https://images.pexels.com/photos/36766695/pexels-photo-36766695.jpeg",
  realestate: "https://images.pexels.com/photos/35699509/pexels-photo-35699509.jpeg",
};

const SERVICES = [
  {
    n: "01",
    title: "Tours",
    desc: "Curated private and group itineraries across Tbilisi, Kakheti, Kazbegi and beyond.",
    icon: MapTrifold,
    img: IMG.tours,
    span: "md:col-span-4",
  },
  {
    n: "02",
    title: "Hotel & Apart Hotel Bookings",
    desc: "Handpicked stays at partner-negotiated rates, across every region of Georgia.",
    icon: Buildings,
    img: IMG.hotel,
    span: "md:col-span-2",
  },
  {
    n: "03",
    title: "Airport Transfers & Private Transportation",
    desc: "Meet & greet, private cars and coaches — on time, every time.",
    icon: AirplaneTakeoff,
    img: IMG.transport,
    span: "md:col-span-2",
  },
  {
    n: "04",
    title: "Corporate Travel & Business Services",
    desc: "Business travel, MICE support and delegation logistics, end to end.",
    icon: Briefcase,
    img: IMG.corporate,
    span: "md:col-span-2",
  },
  {
    n: "05",
    title: "Group Travel & FIT Services",
    desc: "Series groups, ad-hoc departures and fully tailored FIT programmes.",
    icon: UsersThree,
    span: "md:col-span-2",
  },
  {
    n: "06",
    title: "Study Abroad & University Admissions",
    desc: "University placement and admissions support for international students.",
    icon: GraduationCap,
    span: "md:col-span-2",
  },
  {
    n: "07",
    title: "Medical Tourism",
    desc: "Dental, wellness and treatment journeys with trusted clinics and care teams.",
    icon: Tooth,
    span: "md:col-span-2",
  },
  {
    n: "08",
    title: "Real Estate Investment Support",
    desc: "On-ground guidance for international clients investing in Georgian property.",
    icon: HouseLine,
    img: IMG.realestate,
    span: "md:col-span-2",
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function ServicesBento() {
  return (
    <section id="services" data-testid="services-section" className="relative py-24 md:py-36">
      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        <div className="mb-16 max-w-2xl md:mb-20">
          <Reveal>
            <SectionLabel testId="services-label">What we do</SectionLabel>
          </Reveal>
          <Reveal delay={0.1}>
            <h2
              data-testid="services-heading"
              className="mt-6 font-display text-3xl font-light leading-tight tracking-tight text-[#071526] md:text-5xl"
            >
              Eight ways we power your clients'{" "}
              <span className="italic text-[#184167]">Georgia experience.</span>
            </h2>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-6 md:auto-rows-[290px] md:gap-6" data-testid="services-grid">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            const hasImg = Boolean(s.img);
            return (
              <motion.article
                key={s.n}
                custom={i}
                variants={cardVariants}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                data-testid={`service-card-${s.n}`}
                className={`group relative flex min-h-[260px] flex-col justify-between overflow-hidden border border-[#E2E8F0] p-7 shadow-sm transition-[border-color,box-shadow] duration-500 hover:border-[#184167]/50 hover:shadow-md md:min-h-0 ${hasImg ? "" : "bg-white"} ${s.span}`}
              >
                {hasImg && (
                  <>
                    <img
                      src={s.img}
                      alt={s.title}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover opacity-80 transition-[opacity,transform] duration-700 group-hover:scale-105 group-hover:opacity-90"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#071526]/95 via-[#071526]/55 to-[#071526]/15" />
                  </>
                )}
                <div className="relative flex items-start justify-between">
                  <span className={`font-display text-sm italic ${hasImg ? "text-[#A8C6E8]" : "text-[#184167]"}`}>
                    {s.n}
                  </span>
                  <Icon
                    size={26}
                    weight="thin"
                    className={`transition-colors duration-500 ${
                      hasImg
                        ? "text-white/70 group-hover:text-[#A8C6E8]"
                        : "text-[#5C6B7C] group-hover:text-[#184167]"
                    }`}
                  />
                </div>
                <div className="relative">
                  <h3
                    className={`font-display text-xl font-light leading-snug md:text-2xl ${
                      hasImg ? "text-white" : "text-[#071526]"
                    }`}
                  >
                    {s.title}
                  </h3>
                  <p
                    className={`mt-2 max-w-md text-sm font-light leading-relaxed transition-colors duration-500 ${
                      hasImg ? "text-[#C6D2E0] group-hover:text-white" : "text-[#5C6B7C]"
                    }`}
                  >
                    {s.desc}
                  </p>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
